import type { RotatingAlarmRoutine } from "@src/services/api/types";
import {
  makeTimeRange,
  makeAllDayEffectiveRange,
  EffectiveTimeRangesSchema,
  makeDateRecord,
  type RotatingPatternInterval,
  type DateRecord,
  type EffectiveTimeRanges,
} from "@visioni/schedules";
import {
  GridState,
  isAllDayRange,
  isTimeRange,
  markAllDaySlots,
  markTimeRangeSlots,
  convertSlotsToTimeRanges,
} from "../grid-utils";
import { type Granularity, getSlotsPerDay } from "../granularity-utils";

function hasTimeRanges(
  interval: RotatingPatternInterval,
): interval is RotatingPatternInterval & { time_ranges: EffectiveTimeRanges } {
  return "time_ranges" in interval && interval.time_ranges !== undefined;
}

export function parseRotatingRoutineToGridState(
  routineData: RotatingAlarmRoutine,
  granularity: Granularity = 60,
): GridState {
  const gridState: GridState = {};
  let dayIndex = 0;
  const slotsPerDay = getSlotsPerDay(granularity);

  routineData.pattern.forEach((interval: RotatingPatternInterval) => {
    for (let d = 0; d < interval.days; d++) {
      if (hasTimeRanges(interval)) {
        // EffectiveTimeRanges is an array-like structure
        const timeRangesArray: EffectiveTimeRanges = interval.time_ranges;

        timeRangesArray.forEach((range) => {
          if (isAllDayRange(range)) {
            markAllDaySlots(gridState, dayIndex, slotsPerDay);
          } else if (isTimeRange(range)) {
            const timeRange = range as {
              start: { hour: number; minute: number };
              end: { hour: number; minute: number };
            };
            markTimeRangeSlots(gridState, dayIndex, timeRange, granularity);
          }
        });
      }
      dayIndex++;
    }
  });

  return gridState;
}

export function calculateTotalDays(routineData: RotatingAlarmRoutine): number {
  return routineData.pattern.reduce((sum, interval) => sum + interval.days, 0);
}

export function convertGridStateToRotatingRoutine(
  gridState: GridState,
  numDays: number,
  originalStart?: DateRecord,
  granularity: Granularity = 60,
): RotatingAlarmRoutine {
  const pattern: RotatingPatternInterval[] = [];
  const slotsPerDay = getSlotsPerDay(granularity);

  // Group consecutive days with similar patterns
  let currentPattern: { days: number[]; slots: Set<number> } | null = null;

  for (let day = 0; day < numDays; day++) {
    const slotsForDay = new Set<number>();
    for (let slot = 0; slot < slotsPerDay; slot++) {
      if (gridState[`${day}-${slot}`]) {
        slotsForDay.add(slot);
      }
    }

    // Check if this day matches the current pattern
    const slotsArray = Array.from(slotsForDay).sort((a, b) => a - b);
    const currentSlotsArray = currentPattern ? Array.from(currentPattern.slots).sort((a, b) => a - b) : [];

    const isSamePattern =
      slotsArray.length === currentSlotsArray.length && slotsArray.every((slot, i) => slot === currentSlotsArray[i]);

    if (currentPattern && isSamePattern) {
      currentPattern.days.push(day);
    } else {
      // Save previous pattern if exists
      if (currentPattern) {
        addPatternInterval(pattern, currentPattern, granularity);
      }

      // Start new pattern
      currentPattern = {
        days: [day],
        slots: slotsForDay,
      };
    }
  }

  // Save the last pattern
  if (currentPattern) {
    addPatternInterval(pattern, currentPattern, granularity);
  }

  // Preserve the original start date if provided, otherwise use current date
  const startDate =
    originalStart || makeDateRecord(new Date().getFullYear(), new Date().getMonth() + 1, new Date().getDate());

  return {
    kind: "rotating",
    start: startDate,
    pattern:
      pattern.length > 0 ?
        pattern
      : [
          {
            days: numDays,
            time_ranges: makeAllDayEffectiveRange(),
          },
        ],
  };
}

function addPatternInterval(
  pattern: RotatingPatternInterval[],
  currentPattern: { days: number[]; slots: Set<number> },
  granularity: Granularity,
): void {
  if (currentPattern.slots.size > 0) {
    const ranges = convertSlotsToTimeRanges(Array.from(currentPattern.slots), granularity);
    // Ensure we have at least one range before creating EffectiveTimeRanges
    if (ranges.length > 0) {
      pattern.push({
        days: currentPattern.days.length,
        time_ranges: EffectiveTimeRangesSchema.parse(
          ranges as [ReturnType<typeof makeTimeRange>, ...ReturnType<typeof makeTimeRange>[]],
        ),
      });
    } else {
      pattern.push({
        days: currentPattern.days.length,
        time_ranges: makeAllDayEffectiveRange(),
      });
    }
  } else if (currentPattern.days.length > 0) {
    pattern.push({
      days: currentPattern.days.length,
      time_ranges: makeAllDayEffectiveRange(),
    });
  }
}
